import { useEffect, useMemo, useState } from 'react';
import {
  createLansa,
  deleteLansa,
  fetchLansas,
  updateLansa,
} from '../services/lansaService';

function useLansas() {
  const [lansas, setLansas] = useState([]);
  const [formValues, setFormValues] = useState({ name: '' });
  const [formErrors, setFormErrors] = useState({});
  const [editingLansa, setEditingLansa] = useState(null);
  const [editValues, setEditValues] = useState({ name: '' });
  const [editErrors, setEditErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadLansas() {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchLansas();

        if (isMounted) {
          setLansas(response);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadLansas();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setSuccessMessage(''), 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  const sortedLansas = useMemo(
    () => [...lansas].sort((first, second) => first.name.localeCompare(second.name)),
    [lansas]
  );

  function handleFormChange(event) {
    setFormValues({ name: event.target.value });
    setFormErrors({});
    setSubmitError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateLansaForm(formValues, lansas);

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const createdLansa = await createLansa({ name: formValues.name.trim() });

      setLansas((currentLansas) => [...currentLansas, createdLansa]);
      setFormValues({ name: '' });
      setSuccessMessage('Lansa name added successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function openEditModal(lansa) {
    setEditingLansa(lansa);
    setEditValues({ name: lansa.name });
    setEditErrors({});
    setSubmitError('');
  }

  function closeEditModal() {
    setEditingLansa(null);
    setEditValues({ name: '' });
    setEditErrors({});
  }

  function handleEditChange(event) {
    setEditValues({ name: event.target.value });
    setEditErrors({});
    setSubmitError('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    const nextErrors = validateLansaForm(editValues, lansas, editingLansa?.id);

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const updatedLansa = await updateLansa(editingLansa.id, {
        name: editValues.name.trim(),
      });

      setLansas((currentLansas) =>
        currentLansas.map((lansa) =>
          lansa.id === updatedLansa.id ? updatedLansa : lansa
        )
      );
      closeEditModal();
      setSuccessMessage('Lansa name updated successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(lansa) {
    setIsSaving(true);
    setSubmitError('');

    try {
      await deleteLansa(lansa.id);
      setLansas((currentLansas) =>
        currentLansas.filter((currentLansa) => currentLansa.id !== lansa.id)
      );
      setSuccessMessage('Lansa name deleted successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  return {
    lansas: sortedLansas,
    formValues,
    formErrors,
    editingLansa,
    editValues,
    editErrors,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    handleFormChange,
    handleSubmit,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
  };
}

function validateLansaForm(values, lansas = [], currentLansaId = null) {
  const errors = {};
  const normalizedName = values.name.trim();

  if (!normalizedName) {
    errors.name = 'Lansa name is required.';
    return errors;
  }

  const hasDuplicateName = lansas.some((lansa) => {
    if (currentLansaId !== null && lansa.id === currentLansaId) {
      return false;
    }

    return String(lansa.name || '').trim().toLowerCase() === normalizedName.toLowerCase();
  });

  if (hasDuplicateName) {
    errors.name = 'This lansa name already exists.';
  }

  return errors;
}

export default useLansas;
